import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { userService } from "../../../_services";
import DownloadReportDialog from './DownloadReportDialog';
import TierReportDialog from './TierReportDialog';
import CommissionPayoutDialog from './CommissionPayoutDialog';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
    Users, 
    ShoppingCart, 
    FileText, 
    TrendingUp, 
    Download,
    DollarSign,
    UserCheck,
    Calendar,
    Loader2
} from "lucide-react";

/**
 * Reports Dashboard Page
 * Modernized with shadcn components - Central hub for all admin reports
 */
function Reports({ onCommissionClick, onNLSClick, onWiseW9Click }) {
    // State
    const [showDownloadPopup, setShowDownloadPopup] = useState(false);
    const [showTierReportPopup, setShowTierReportPopup] = useState(false);
    const [showCommissionPayoutPopup, setShowCommissionPayoutPopup] = useState(false);
    const [reportTitle, setReportTitle] = useState('');
    const [loading6m, setLoading6m] = useState(false);
    const [isDevEnvironment, setIsDevEnvironment] = useState(false);

    useEffect(() => {
        const currentUrl = window.location.href;
        if (currentUrl.toLowerCase().includes("localhost:") || currentUrl.toLowerCase().includes("dev-backoffice") || currentUrl.toLowerCase().includes("shadcn-backoffice")) {
            setIsDevEnvironment(true);
        }
        
        // Load required APIs on page load
        loadPageData();
    }, []);

    const loadPageData = async () => {
        // Preload is handled by individual views (CommissionTrackingView, etc.)
        // when they mount, so no preload calls needed here.
    };

    const handleReportClick = (title) => {
        setReportTitle(title);
        setShowDownloadPopup(true);
    };

    const handleTierReportClick = (title) => {
        setReportTitle(title);
        setShowTierReportPopup(true);
    };

    const handleCommissionPayoutClick = (title) => {
        setReportTitle(title);
        setShowCommissionPayoutPopup(true);
    };

    const handleDownload6MonthReport = async () => {
        setLoading6m(true);
        try {
            const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
            const fileExtension = '.xlsx';
            
            const apidata = await userService.GetTransaction6m();
            let data_arr = [];

            if (apidata && apidata.length > 0) {
                let h_arr = [];
                data_arr = apidata[0]["transactions"];
                h_arr.push("DistributorName", "EmailId", "Phone No.", "Signup Date");
                
                for (var iter = 4; iter < apidata[0]["headers"].length; iter++) {
                    h_arr.push(apidata[0]["headers"][iter]);
                }

                data_arr = data_arr.map(function (obj) {
                    obj[h_arr[3]] = obj['signUp_Date'].split('T')[0];
                    obj[h_arr[4]] = obj['glic_Trxs_Month1'];
                    obj[h_arr[5]] = obj['singlecare_Trxs_Month1'];
                    obj[h_arr[6]] = obj['glic_Trxs_Month2'];
                    obj[h_arr[7]] = obj['singlecare_Trxs_Month2'];
                    obj[h_arr[8]] = obj['glic_Trxs_Month3'];
                    obj[h_arr[9]] = obj['singlecare_Trxs_Month3'];
                    obj[h_arr[10]] = obj['glic_Trxs_Month4'];
                    obj[h_arr[11]] = obj['singlecare_Trxs_Month4'];
                    obj[h_arr[12]] = obj['glic_Trxs_Month5'];
                    obj[h_arr[13]] = obj['singlecare_Trxs_Month5'];
                    obj[h_arr[14]] = obj['glic_Trxs_Month6'];
                    obj[h_arr[15]] = obj['singlecare_Trxs_Month6'];

                    delete obj['glic_Trxs_Month1']; delete obj['singlecare_Trxs_Month1'];
                    delete obj['glic_Trxs_Month2']; delete obj['singlecare_Trxs_Month2'];
                    delete obj['glic_Trxs_Month3']; delete obj['singlecare_Trxs_Month3'];
                    delete obj['glic_Trxs_Month4']; delete obj['singlecare_Trxs_Month4'];
                    delete obj['glic_Trxs_Month5']; delete obj['singlecare_Trxs_Month5'];
                    delete obj['glic_Trxs_Month6']; delete obj['singlecare_Trxs_Month6'];
                    delete obj['signUp_Date'];
                    return obj;
                });

                const ws = XLSX.utils.json_to_sheet(data_arr);
                const wb = { Sheets: { 'Bin_Wise_Paid_Claims': ws }, SheetNames: ['Bin_Wise_Paid_Claims'] };
                const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
                const data = new Blob([excelBuffer], { type: fileType });
                FileSaver.saveAs(data, 'User_Transactions_by_BIN_Last_6_months_' + moment(new Date()).format("yyyyMMDDhhmmss") + fileExtension);
            }
        } catch (error) {
            console.error('Error downloading 6 month report:', error);
        } finally {
            setLoading6m(false);
        }
    };

    // Auth check
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate replace to="/signup" />;
    }

    if (window.atob(localStorage.getItem('UserRole')) !== "Admin") {
        return <Navigate replace to="/page-not-found" />;
    }

    localStorage.setItem('pagename', 'reportspage');

    const reportCards = [
        {
            title: 'User Transactions by BIN',
            icon: Users,
            description: 'View user transactions by BIN',
            action: () => handleReportClick('User Transactions by BIN'),
            color: 'bg-blue-50'
        },
        {
            title: 'Groups with no Transactions',
            icon: FileText,
            description: 'View groups without transactions',
            action: () => handleReportClick('Groups with no Transactions'),
            color: 'bg-purple-50'
        },
        {
            title: 'User Tier Report',
            icon: TrendingUp,
            description: 'View user tier information',
            action: () => handleTierReportClick('User Tier'),
            color: 'bg-green-50'
        },
        {
            title: 'User Transactions by BIN (6 months)',
            icon: Calendar,
            description: 'Download 6 months transaction history',
            action: handleDownload6MonthReport,
            color: 'bg-yellow-50',
            loading: loading6m
        },
        {
            title: 'Commission Tracking',
            icon: DollarSign,
            description: 'Track commission details',
            action: onCommissionClick,
            color: 'bg-green-50'
        },
        {
            title: 'Users Transactions',
            icon: Users,
            description: 'View all user transactions',
            action: () => handleReportClick('Users Transactions'),
            color: 'bg-blue-50'
        },
        {
            title: 'Parent Referral Report',
            icon: UserCheck,
            description: 'View parent referral details',
            action: () => handleReportClick('Parent Referral'),
            color: 'bg-indigo-50'
        },
        {
            title: 'NLS Rx Marketplace Orders',
            icon: ShoppingCart,
            description: 'View NLS marketplace orders',
            action: onNLSClick,
            color: 'bg-sidebar-accent'
        },
        {
            title: 'Commission Payout Report',
            icon: DollarSign,
            description: 'View commission payout details',
            action: () => handleCommissionPayoutClick('Commission Payout'),
            color: 'bg-green-50'
        },
        {
            title: 'Wise & W-9 Report',
            icon: FileText,
            description: 'View Wise and W-9 information',
            action: onWiseW9Click,
            color: 'bg-muted/30'
        },
    ];

    // Add conditional report for dev environment
    if (isDevEnvironment) {
        reportCards.splice(7, 0, {
            title: 'Minimum Payout Report',
            icon: Download,
            description: 'View minimum payout information',
            action: () => handleTierReportClick('Minimum Payout'),
            color: 'bg-red-50'
        });
    }

    return (
        <div className="w-full space-y-6">
            {/* Heading */}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Reports</h1>

            {/* Report Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4 gap-4">
                {reportCards.map((report, index) => (
                    <Card 
                        key={index} 
                        className="hover:shadow-lg transition-shadow cursor-pointer border-muted relative"
                        onClick={report.action}
                    >
                        {report.loading && (
                            <Loader2 className="absolute top-3 right-3 h-4 w-4 animate-spin text-accent" />
                        )}
                        <CardContent className="p-6">
                            <div className="flex items-start gap-4">
                                <div className={`p-3 rounded-lg  bg-sidebar-accent`}>
                                    <report.icon className="h-6 w-6 text-primary" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-primary mb-1">
                                        {report.title}
                                    </h3>
                                    <p className="text-sm text-primary/70">
                                        {report.description}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Modals - Modernized with shadcn Dialog */}
            <DownloadReportDialog
                open={showDownloadPopup}
                onOpenChange={setShowDownloadPopup}
                title={reportTitle}
            />

            <TierReportDialog
                open={showTierReportPopup}
                onOpenChange={setShowTierReportPopup}
                title={reportTitle}
            />

            <CommissionPayoutDialog
                open={showCommissionPayoutPopup}
                onOpenChange={setShowCommissionPayoutPopup}
                title={reportTitle}
            />
        </div>
    );
}

export default Reports;
